import { memo, useCallback, useRef } from 'react'
import { hapticLight } from '@/telegram'
import { usePlaceBet } from '@/hooks/usePlaceBet'
import { cn } from '@/lib/utils'
import type { MatrixCell } from '@/lib/rangeMarketUtils'

const LONG_PRESS_MS = 1000

interface StrikeMatrixProps {
  matrix: MatrixCell[][]
  disabled?: boolean
  dayLabels?: [string, string, string]
  onCellTap?: (cell: MatrixCell, side?: 'YES' | 'NO') => void
  selectedCellIds?: Set<string>
  onToggleSelection?: (cellId: string) => void
}

export const StrikeMatrix = memo(function StrikeMatrix({
  matrix,
  disabled,
  dayLabels: _dayLabels = ['Today', '+1', '+2'],
  onCellTap,
  selectedCellIds,
  onToggleSelection,
}: StrikeMatrixProps) {
  const placeBet = usePlaceBet()
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressHandledRef = useRef(false)

  const handleTap = useCallback(
    (cell: MatrixCell, side: 'YES' | 'NO') => {
      if (disabled || placeBet.isPending) return
      const tokenId = side === 'YES' ? cell.yesTokenId : cell.noTokenId
      if (!tokenId) return
      hapticLight()
      placeBet.mutate({
        tokenId,
        side,
        amountUsd: 5,
        tickSize: cell.tickSize,
        negRisk: cell.negRisk,
        yesPrice: cell.yesPrice,
        noPrice: cell.noPrice,
      })
    },
    [disabled, placeBet]
  )

  if (!matrix.length) return null
  const numCols = matrix[0]?.length ?? 1

  return (
    <div
      className="grid w-full"
      style={{
        gridTemplateColumns: `repeat(${numCols}, minmax(44px, 1fr))`,
        gap: 0,
      }}
    >
      {matrix.flatMap((row, ri) =>
        row.map((cell, ci) => {
          const hasOrder = !!cell.orderInfo || !!cell.orderInfoNo
          const hasPosition = !!cell.positionInfo
          const hasPositionNo = !!cell.positionInfoNo
          const cellBg = hasOrder
            ? 'bg-amber-500/20'
            : hasPosition
              ? 'bg-[#22c55e]/55'
              : hasPositionNo
                ? 'bg-red-500/30'
                : 'bg-gray-500/15'
          const isSelected = selectedCellIds?.has(cell.id)
          const yesPriceStr = cell.yesTokenId ? (cell.yesPrice * 100).toFixed(1) : '—'
          const noPriceStr = cell.noTokenId ? (cell.noPrice * 100).toFixed(1) : '—'
          const showYesPrice = !(cell.orderInfoNo || cell.positionInfoNo)
          const showNoPrice = !(cell.orderInfo || cell.positionInfo)
          const priceDisplay = cell.yesTokenId || cell.noTokenId ? (
            <span className="flex flex-col items-center gap-0 text-sm font-semibold">
              {showYesPrice && (
                <span className="flex items-center gap-0.5 leading-tight">
                  <span className="text-emerald-400">{yesPriceStr}</span>
                </span>
              )}
              {showYesPrice && showNoPrice && <span className="w-full border-b border-gray-500/50" style={{ minWidth: 20 }} />}
              {showNoPrice && (
                <span className="flex items-center gap-0.5 leading-tight">
                  <span className="text-red-400">{noPriceStr}</span>
                </span>
              )}
            </span>
          ) : '—'
          const startLongPress = () => {
            longPressHandledRef.current = false
            longPressTimerRef.current = setTimeout(() => {
              onToggleSelection?.(cell.id)
              longPressHandledRef.current = true
              longPressTimerRef.current = null
            }, LONG_PRESS_MS)
          }
          const clearLongPress = () => {
            if (longPressTimerRef.current) {
              clearTimeout(longPressTimerRef.current)
              longPressTimerRef.current = null
            }
          }
          return (
            <div
              key={`${cell.id}-${ri}-${ci}`}
              className={cn(
                'flex aspect-square min-w-0 flex-col rounded-none',
                cellBg
              )}
              style={{
                minWidth: 44,
                minHeight: 44,
                border: '0.5px solid rgba(148,163,184,0.1)',
                ...(isSelected && { boxShadow: '4px 0 0 0 #b87333' }),
              }}
            >
              <div className="flex shrink-0 flex-col items-center justify-center py-0.5">
                {priceDisplay}
              </div>
              <button
                type="button"
                onPointerDown={startLongPress}
                onPointerUp={clearLongPress}
                onPointerLeave={clearLongPress}
                onPointerCancel={clearLongPress}
                onClick={() => {
                  if (longPressHandledRef.current) {
                    longPressHandledRef.current = false
                    return
                  }
                  if (onCellTap) {
                    hapticLight()
                    onCellTap(cell, 'YES')
                  } else {
                    handleTap(cell, 'YES')
                  }
                }}
                disabled={disabled || placeBet.isPending || !cell.yesTokenId}
                className={cn(
                  'flex flex-1 flex-col items-center justify-center text-center rounded-none',
                  'active:scale-[0.98] transition-transform',
                  (disabled || placeBet.isPending || !cell.yesTokenId) && 'opacity-60 cursor-not-allowed'
                )}
              >
                <span className="text-sm font-bold text-white">YES</span>
                {cell.orderInfo && (
                  <span className="text-center text-[10px] text-amber-200/90">
                    {(cell.orderInfo.orderPrice * 100).toFixed(1)} {Math.round(cell.orderInfo.sizeMatched)}/{Math.round(cell.orderInfo.sizeTotal)}
                  </span>
                )}
                {!cell.orderInfo && cell.positionInfo && (
                  <span className="text-center text-[10px] text-emerald-200/90">
                    {(cell.positionInfo.avgPrice * 100).toFixed(1)} {Math.round(cell.positionInfo.size)}
                  </span>
                )}
              </button>
              <button
                type="button"
                onPointerDown={startLongPress}
                onPointerUp={clearLongPress}
                onPointerLeave={clearLongPress}
                onPointerCancel={clearLongPress}
                onClick={() => {
                  if (longPressHandledRef.current) {
                    longPressHandledRef.current = false
                    return
                  }
                  if (onCellTap) {
                    hapticLight()
                    onCellTap(cell, 'NO')
                  } else {
                    handleTap(cell, 'NO')
                  }
                }}
                disabled={disabled || placeBet.isPending || !cell.noTokenId}
                className={cn(
                  'flex flex-1 flex-col items-center justify-center',
                  'active:scale-[0.98] transition-transform',
                  (disabled || placeBet.isPending || !cell.noTokenId) && 'opacity-60 cursor-not-allowed'
                )}
              >
                <span className="text-sm font-bold text-white">NO</span>
                {cell.orderInfoNo && (
                  <span className="text-center text-[10px] text-amber-200/90">
                    {(cell.orderInfoNo.orderPrice * 100).toFixed(1)} {Math.round(cell.orderInfoNo.sizeMatched)}/{Math.round(cell.orderInfoNo.sizeTotal)}
                  </span>
                )}
                {!cell.orderInfoNo && cell.positionInfoNo && (
                  <span className="text-center text-[10px] text-red-200/90">
                    {(cell.positionInfoNo.avgPrice * 100).toFixed(1)} {Math.round(cell.positionInfoNo.size)}
                  </span>
                )}
              </button>
            </div>
          )
        })
      )}
    </div>
  )
})
